/**
 * XRInstallOnboarding — v10.10.0
 * Onboarding paso a paso "Add to Home Screen" para instalar la PWA XR en
 * iOS Safari y Android Chrome. Detecta la plataforma y muestra la guía
 * correcta con capturas SVG in-line + botón de permiso de notificaciones.
 *
 * Se descarta con localStorage `xr_onboarding_dismissed_v1` si el usuario
 * lo cierra. Reaparece automáticamente en la próxima carga si NO se marcó
 * "no volver a mostrar".
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/ui/button';
import { X, Share, PlusSquare, DotsThreeVertical, DownloadSimple, Bell, CheckCircle, AppleLogo, AndroidLogo, WarningCircle } from '@phosphor-icons/react';

const DISMISS_KEY = 'xr_onboarding_dismissed_v1';

function detectPlatform() {
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isIOSSafari = isIOS && /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);
  const isAndroid = /Android/.test(ua);
  const isStandalone = window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
  return { isIOS, isIOSSafari, isAndroid, isStandalone, isDesktop: !isIOS && !isAndroid };
}

/* ---------- Guías visuales SVG in-line ---------- */
function IOSShareIllustration() {
  return (
    <svg viewBox="0 0 260 140" className="w-full max-w-xs mx-auto" aria-hidden="true">
      <rect x="4" y="4" width="252" height="132" rx="14" fill="#0f172a" stroke="#164e63" strokeWidth="2" />
      <rect x="12" y="12" width="236" height="100" rx="6" fill="#0e7490" opacity="0.15" />
      <text x="130" y="60" textAnchor="middle" fill="#67e8f9" fontSize="10" fontFamily="monospace">SimuClinic XR</text>
      <rect x="12" y="116" width="236" height="16" rx="2" fill="#1e293b" />
      {/* Share icon highlight */}
      <g transform="translate(114, 118)">
        <rect x="-10" y="-4" width="20" height="20" rx="3" fill="#06b6d4" opacity="0.35">
          <animate attributeName="opacity" values="0.15;0.8;0.15" dur="1.6s" repeatCount="indefinite" />
        </rect>
        <path d="M0,-2 v10 M-4,4 l4,-6 l4,6" stroke="#67e8f9" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="-4" y="4" width="8" height="6" rx="1" stroke="#67e8f9" strokeWidth="1.4" fill="none" />
      </g>
      <text x="114" y="140" textAnchor="middle" fill="#67e8f9" fontSize="9">Botón Compartir</text>
    </svg>
  );
}

function IOSAddToHomeIllustration() {
  return (
    <svg viewBox="0 0 260 140" className="w-full max-w-xs mx-auto" aria-hidden="true">
      <rect x="4" y="4" width="252" height="132" rx="14" fill="#0f172a" stroke="#164e63" strokeWidth="2" />
      <rect x="20" y="20" width="220" height="100" rx="8" fill="#1e293b" />
      <text x="130" y="40" textAnchor="middle" fill="#94a3b8" fontSize="9" fontWeight="bold">Compartir</text>
      <rect x="28" y="52" width="204" height="26" rx="4" fill="#334155" />
      <text x="40" y="68" fill="#e2e8f0" fontSize="10">Copiar</text>
      <rect x="28" y="82" width="204" height="26" rx="4" fill="#06b6d4">
        <animate attributeName="opacity" values="0.7;1;0.7" dur="1.4s" repeatCount="indefinite" />
      </rect>
      <text x="40" y="98" fill="#0f172a" fontSize="10" fontWeight="bold">Agregar a inicio</text>
      <g transform="translate(216, 95)">
        <rect x="-6" y="-6" width="12" height="12" rx="2" stroke="#0f172a" strokeWidth="1.4" fill="none" />
        <path d="M0,-3 v6 M-3,0 h6" stroke="#0f172a" strokeWidth="1.4" strokeLinecap="round" />
      </g>
    </svg>
  );
}

function AndroidMenuIllustration() {
  return (
    <svg viewBox="0 0 260 140" className="w-full max-w-xs mx-auto" aria-hidden="true">
      <rect x="4" y="4" width="252" height="132" rx="14" fill="#0f172a" stroke="#164e63" strokeWidth="2" />
      <rect x="12" y="12" width="236" height="20" rx="3" fill="#1e293b" />
      <text x="16" y="26" fill="#94a3b8" fontSize="10" fontFamily="monospace">simuclinic.app/xr</text>
      <g transform="translate(232, 22)">
        <circle cx="0" cy="-4" r="1.8" fill="#67e8f9">
          <animate attributeName="r" values="1.8;3;1.8" dur="1.4s" repeatCount="indefinite" />
        </circle>
        <circle cx="0" cy="0" r="1.8" fill="#67e8f9" />
        <circle cx="0" cy="4" r="1.8" fill="#67e8f9" />
      </g>
      <rect x="12" y="40" width="236" height="88" rx="4" fill="#0e7490" opacity="0.2" />
      <text x="130" y="88" textAnchor="middle" fill="#67e8f9" fontSize="10">Menú ⋮ → Instalar app</text>
    </svg>
  );
}

/* ---------- Componente principal ---------- */
export default function XRInstallOnboarding({ open, onClose, force = false }) {
  const platform = useMemo(() => detectPlatform(), []);
  const [step, setStep] = useState(0);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [notifStatus, setNotifStatus] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onBeforeInstall = (e) => { e.preventDefault(); setInstallPrompt(e); };
    const onInstalled = () => setInstalled(true);
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const doInstall = async () => {
    if (!installPrompt) return;
    try {
      await installPrompt.prompt();
      const res = await installPrompt.userChoice;
      if (res?.outcome === 'accepted') setInstalled(true);
    } catch { /* ignore */ }
    setInstallPrompt(null);
  };

  const requestNotifPermission = async () => {
    if (typeof Notification === 'undefined') return;
    try {
      const p = await Notification.requestPermission();
      setNotifStatus(p);
    } catch { /* ignore */ }
  };

  const dismiss = (permanent = false) => {
    if (permanent) {
      try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* noop */ }
    }
    onClose?.();
  };

  if (!open) return null;
  if (platform.isStandalone && !force) return null;

  /* Estructura de pasos por plataforma */
  const steps = platform.isIOS
    ? [
        {
          title: 'Toca el botón Compartir',
          body: (
            <>
              <p className="text-sm text-cyan-100 mb-3">
                En Safari, toca <Share size={14} weight="bold" className="inline mx-1 text-cyan-300" />
                en la barra inferior.
              </p>
              <IOSShareIllustration />
            </>
          ),
          hint: 'Si no ves el botón, asegúrate de estar usando Safari (no Chrome ni Firefox en iOS).',
        },
        {
          title: 'Elige "Agregar a inicio"',
          body: (
            <>
              <p className="text-sm text-cyan-100 mb-3">
                Desplázate en el menú y pulsa <PlusSquare size={14} weight="bold" className="inline mx-1 text-cyan-300" />
                <strong>Agregar a pantalla de inicio</strong>.
              </p>
              <IOSAddToHomeIllustration />
            </>
          ),
          hint: 'La app aparecerá en tu Home como un ícono independiente.',
        },
        {
          title: 'Activar notificaciones (opcional)',
          body: (
            <div className="space-y-3">
              <p className="text-sm text-cyan-100">
                Recibe avisos del instructor (pausas, mensajes, cambios) aunque la app esté minimizada.
              </p>
              {notifStatus === 'granted' ? (
                <div className="flex items-center gap-2 p-3 rounded bg-emerald-900/40 border border-emerald-700 text-emerald-200 text-sm" data-testid="notif-status-granted">
                  <CheckCircle size={18} weight="fill" /> Notificaciones activas
                </div>
              ) : notifStatus === 'denied' ? (
                <div className="flex items-center gap-2 p-3 rounded bg-red-900/40 border border-red-700 text-red-200 text-sm">
                  <WarningCircle size={18} /> Bloqueadas — actívalas en Ajustes → Safari → Notificaciones.
                </div>
              ) : (
                <Button onClick={requestNotifPermission} className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold" data-testid="notif-request-btn">
                  <Bell size={16} className="mr-2" /> Activar notificaciones
                </Button>
              )}
              <p className="text-[11px] text-slate-400">Nota: en iOS las notificaciones PWA requieren iOS 16.4+.</p>
            </div>
          ),
        },
      ]
    : platform.isAndroid
    ? [
        {
          title: 'Instalar SimuClinic XR',
          body: (
            <div className="space-y-3">
              <p className="text-sm text-cyan-100">
                {installPrompt
                  ? 'Chrome puede instalar la app automáticamente. Pulsa el botón:'
                  : 'Abre el menú ⋮ y elige "Instalar app" o "Añadir a pantalla de inicio".'}
              </p>
              {installPrompt ? (
                <Button onClick={doInstall} className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold" data-testid="install-native-btn">
                  <DownloadSimple size={16} className="mr-2" weight="bold" /> Instalar ahora
                </Button>
              ) : (
                <AndroidMenuIllustration />
              )}
              {installed && (
                <div className="flex items-center gap-2 p-2 rounded bg-emerald-900/40 border border-emerald-700 text-emerald-200 text-sm">
                  <CheckCircle size={16} weight="fill" /> App instalada
                </div>
              )}
            </div>
          ),
          hint: 'Si el botón no aparece, sirve la URL sobre HTTPS y evita modo incógnito.',
        },
        {
          title: 'Activar notificaciones',
          body: (
            <div className="space-y-3">
              <p className="text-sm text-cyan-100">
                Recibe avisos del instructor durante la sesión (pausas, mensajes, cambios de estado).
              </p>
              {notifStatus === 'granted' ? (
                <div className="flex items-center gap-2 p-3 rounded bg-emerald-900/40 border border-emerald-700 text-emerald-200 text-sm" data-testid="notif-status-granted">
                  <CheckCircle size={18} weight="fill" /> Notificaciones activas
                </div>
              ) : notifStatus === 'denied' ? (
                <div className="flex items-center gap-2 p-3 rounded bg-red-900/40 border border-red-700 text-red-200 text-sm">
                  <WarningCircle size={18} /> Bloqueadas — actívalas en Ajustes de Chrome → Notificaciones.
                </div>
              ) : (
                <Button onClick={requestNotifPermission} className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold" data-testid="notif-request-btn">
                  <Bell size={16} className="mr-2" /> Activar notificaciones
                </Button>
              )}
            </div>
          ),
        },
      ]
    : [
        {
          title: 'Escanea desde tu móvil',
          body: (
            <div className="space-y-3">
              <p className="text-sm text-cyan-100">
                SimuClinic XR está diseñado para móvil/tablet. Abre esta URL en tu dispositivo
                y sigue las instrucciones para instalar la PWA.
              </p>
              <div className="p-3 rounded bg-slate-800 border border-slate-700 font-mono text-xs break-all text-cyan-300">
                {window.location.origin}/xr
              </div>
              <p className="text-[11px] text-slate-400">Requiere HTTPS. Compatible con iOS 16+ Safari y Android Chrome/Edge.</p>
            </div>
          ),
        },
      ];

  const totalSteps = steps.length;
  const cur = steps[step];
  const platformIcon = platform.isIOS ? <AppleLogo size={16} weight="fill" className="text-slate-300" />
    : platform.isAndroid ? <AndroidLogo size={16} weight="fill" className="text-emerald-400" />
    : <DownloadSimple size={16} className="text-slate-300" />;
  const platformLabel = platform.isIOS ? 'iOS Safari' : platform.isAndroid ? 'Android' : 'Desktop';

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" data-testid="xr-onboarding-modal">
      <div className="w-full max-w-md bg-gradient-to-b from-slate-900 to-slate-950 border border-cyan-800 rounded-2xl overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            {platformIcon}
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-cyan-300">Instalar SimuClinic XR</div>
              <div className="text-[10px] text-slate-400">{platformLabel} · paso {step + 1} de {totalSteps}</div>
            </div>
          </div>
          <button onClick={() => dismiss(false)} className="p-1 rounded hover:bg-slate-800 text-slate-400" data-testid="onboarding-close-btn" aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4" data-testid={`onboarding-step-${step}`}>
          <h2 className="text-lg font-bold text-white">{cur.title}</h2>
          <div>{cur.body}</div>
          {cur.hint && (
            <p className="text-[11px] text-amber-200/70 border-l-2 border-amber-500 pl-2">{cur.hint}</p>
          )}
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-1.5 pb-2">
          {steps.map((_, i) => (
            <div key={i} className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-cyan-400' : 'w-1.5 bg-slate-700'}`} />
          ))}
        </div>

        <div className="p-4 border-t border-slate-800 flex items-center justify-between gap-2">
          <button
            onClick={() => dismiss(true)}
            className="text-[11px] text-slate-500 hover:text-slate-300 underline"
            data-testid="onboarding-dismiss-forever"
          >
            No mostrar de nuevo
          </button>
          <div className="flex gap-2">
            {step > 0 && (
              <Button variant="outline" size="sm" onClick={() => setStep(step - 1)} className="text-slate-900" data-testid="onboarding-prev-btn">
                Atrás
              </Button>
            )}
            {step < totalSteps - 1 ? (
              <Button size="sm" onClick={() => setStep(step + 1)} className="bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold" data-testid="onboarding-next-btn">
                Siguiente
              </Button>
            ) : (
              <Button size="sm" onClick={() => dismiss(true)} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold" data-testid="onboarding-finish-btn">
                Listo
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Helper: decidir si mostrar automáticamente ---------- */
export function shouldShowXRInstallOnboarding() {
  try {
    if (localStorage.getItem(DISMISS_KEY) === '1') return false;
  } catch { /* noop */ }
  const isStandalone = window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
  if (isStandalone) return false;
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  return isMobile;
}
